import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Briefcase, MapPin, DollarSign, Clock, Filter } from "lucide-react";
import { toast } from "sonner";

const SPECIALTIES = ["ICU", "ER", "Med-Surg", "OR", "Cath Lab", "Respiratory Therapy", "Physical Therapy", "Radiology"];
const CATEGORIES = ["Travel", "Local", "Per diem", "Contract", "Permanent"];
const STATES = ["TX", "CA", "NY", "FL", "IL", "AZ", "GA", "PA", "OH", "MI"];

export default function JobMarketplace() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [filters, setFilters] = useState({ specialty: "", category: "", state: "" });
  const [applying, setApplying] = useState(null);

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/jobs", { params });
    setJobs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const apply = async (job_id) => {
    setApplying(job_id);
    try {
      await api.post("/applications", { job_id });
      toast.success("Application submitted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setApplying(null); }
  };

  return (
    <div className="space-y-6" data-testid="job-marketplace">
      <div>
        <div className="overline text-trital-red">Opportunities</div>
        <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Jobs</h1>
        <p className="text-slate-600 text-sm">Travel, contract, per diem, and permanent roles across the US.</p>
      </div>
      <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-card flex flex-wrap items-center gap-3">
        <Filter className="w-4 h-4 text-slate-500" />
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="job-filter-category">
          <option value="">All types</option>
          {CATEGORIES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.specialty} onChange={(e) => setFilters({ ...filters, specialty: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
          <option value="">All specialties</option>
          {SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-300 text-sm">
          <option value="">All states</option>
          {STATES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <button onClick={() => setFilters({ specialty: "", category: "", state: "" })} className="ml-auto text-sm text-slate-500 hover:text-trital-navy">Reset</button>
      </div>

      <div className="grid gap-3">
        {jobs.length === 0 && <div className="text-slate-500 bg-white border border-slate-200 rounded-xl p-6">No jobs match your filters.</div>}
        {jobs.map((j) => (
          <div key={j.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card hover:shadow-card-hover">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-red text-white font-bold uppercase tracking-wider">{j.category}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{j.specialty}</span>
                  {j.contract_length_weeks && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold uppercase tracking-wider">{j.contract_length_weeks} wks</span>}
                </div>
                <div className="font-display font-bold text-xl text-slate-900 flex items-center gap-2"><Briefcase className="w-4 h-4 text-trital-navy" /> {j.title}</div>
                <div className="mt-1 text-xs text-slate-500 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.city}, {j.state}</span>
                  <span>{j.facility_name}</span>
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {j.shift}</span>
                </div>
                <p className="mt-3 text-sm text-slate-600 max-w-3xl line-clamp-2">{j.description}</p>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display font-black text-2xl text-trital-navy flex items-center gap-1 justify-end"><DollarSign className="w-5 h-5" />{j.pay_rate_min}-{j.pay_rate_max}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">per hour</div>
                {user?.role === "clinician" && (
                  <button onClick={() => apply(j.id)} disabled={applying === j.id} className="mt-3 px-4 py-2 rounded-full bg-trital-navy text-white text-sm font-semibold btn-lift hover:bg-trital-navy-deep disabled:opacity-60" data-testid={`apply-${j.id}`}>
                    {applying === j.id ? "Applying…" : "Apply now"}
                  </button>
                )}
              </div>
            </div>
            {j.requirements?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {j.requirements.slice(0, 6).map((r, i) => (
                  <span key={i} className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1 rounded-full">{r}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
